/**
 * TonnieLogo Component
 *
 * Renders the Tonnie logo in various variants and sizes.
 */

import { getBrandLogoAsset } from '../images/brand';
import type { ImageSize, LogoVariant } from '../types';

/**
 * Logo size presets (in pixels)
 */
const LOGO_SIZES: Record<ImageSize, { width: number; height: number }> = {
  xs: { width: 80, height: 20 },
  sm: { width: 120, height: 30 },
  md: { width: 160, height: 40 },
  lg: { width: 200, height: 50 },
  xl: { width: 240, height: 60 },
  '2xl': { width: 320, height: 80 },
};

/**
 * Symbol logo sizes (square)
 */
const SYMBOL_SIZES: Record<ImageSize, number> = {
  xs: 24,
  sm: 32,
  md: 48,
  lg: 64,
  xl: 80,
  '2xl': 120,
};

/**
 * Props for TonnieLogo component
 */
export interface TonnieLogoProps {
  /** Logo variant: horizontal (default), symbol, or wordmark */
  variant?: LogoVariant;
  /** Size preset */
  size?: ImageSize;
  /** Custom width (overrides size preset) */
  width?: number;
  /** Custom height (overrides size preset) */
  height?: number;
  /** Additional CSS classes */
  className?: string;
  /** Optional click handler */
  onClick?: () => void;
}

/**
 * TonnieLogo Component
 *
 * Renders the Tonnie logo with consistent sizing and accessibility.
 *
 * @example
 * ```tsx
 * // Horizontal logo (default)
 * <TonnieLogo />
 * <TonnieLogo variant="horizontal" size="lg" />
 *
 * // Symbol only
 * <TonnieLogo variant="symbol" size="md" />
 *
 * // Custom size
 * <TonnieLogo variant="wordmark" width={200} />
 * ```
 */
export function TonnieLogo({
  variant = 'horizontal',
  size = 'md',
  width,
  height,
  className,
  onClick,
}: TonnieLogoProps) {
  const asset = getBrandLogoAsset(variant);

  // Calculate dimensions
  let finalWidth: number;
  let finalHeight: number;

  if (variant === 'symbol') {
    const symbolSize = width || height || SYMBOL_SIZES[size];
    finalWidth = symbolSize;
    finalHeight = symbolSize;
  } else {
    const presetSize = LOGO_SIZES[size];
    finalWidth = width || presetSize.width;
    finalHeight = height || presetSize.height;
  }

  return (
    <img
      src={asset.path}
      alt={asset.alt}
      width={finalWidth}
      height={finalHeight}
      className={className}
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : undefined }}
      loading="eager"
      decoding="async"
    />
  );
}

export default TonnieLogo;
